import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { VirtualTicketMessageList } from '@/components/tickets/VirtualTicketMessageList';
import { cn } from '@/lib/utils';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import type { TicketMessage } from '@/types/ticket';
import {
  formatTicketMessageTime,
  isMessageSeenByStudent,
} from '@/components/tickets/ticketMessageUtils';

function MessageEditCard({
  editText,
  onEditTextChange,
  onSaveEdit,
  onCancelEdit,
}: {
  editText: string;
  onEditTextChange: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
}) {
  return (
    <div className="w-full max-w-[min(420px,72vw)] min-w-[280px] overflow-hidden rounded-[20px] rounded-br-[7px] border border-border bg-card shadow-[0_12px_28px_rgba(15,23,42,0.12)]">
      <div className="border-b border-border bg-accent px-4 py-2">
        <p className="text-[11px] font-bold tracking-wide text-primary uppercase">
          Edit message
        </p>
      </div>
      <div className="p-4">
        <textarea
          value={editText}
          onChange={(e) => onEditTextChange(e.target.value)}
          rows={4}
          className="min-h-[96px] w-full resize-none rounded-2xl border border-border bg-background px-3.5 py-3 text-sm leading-relaxed text-foreground outline-none focus:border-ring focus:ring-4 focus:ring-ring/20"
          placeholder="Update your message…"
          autoFocus
        />
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancelEdit}
            className="h-9 rounded-full border border-border bg-background px-4 text-xs font-bold text-muted-foreground transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSaveEdit}
            disabled={!editText.trim()}
            className="h-9 rounded-full bg-primary-gradient px-4 text-xs font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function messageDisplayText(m: TicketMessage): string {
  if (m.isDeleted) return 'This message was deleted';
  return m.message;
}

function MessageAttachments({
  attachments,
  isAdmin,
}: {
  attachments: TicketMessage['attachments'];
  isAdmin: boolean;
}) {
  if (!attachments?.length) return null;
  return (
    <div className="mt-2.5 space-y-2">
      {attachments.map((a) => (
        <a
          key={a.attachmentId ?? `${a.filename}-${a.url}`}
          href={a.url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center gap-1.5 rounded-[14px] px-3 py-2.5 text-xs font-bold no-underline',
            isAdmin
              ? 'border border-white/20 bg-white/15 text-white hover:bg-white/20'
              : 'border border-border bg-muted text-foreground hover:bg-muted/80'
          )}
        >
          <span aria-hidden>📎</span>
          {a.filename}
        </a>
      ))}
    </div>
  );
}

type TicketConversationMessagesProps = {
  messages: TicketMessage[];
  hasMoreOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => Promise<boolean | void>;
  scrollToBottomKey?: string | null;
  scrollToBottomSignal?: number;
  onAtBottomChange?: (atBottom: boolean) => void;
  studentLastReadAt?: string | null;
  editingMessageId: string | null;
  editText: string;
  onEditTextChange: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onStartEdit: (message: TicketMessage) => void;
  onUnsend: (messageId: string) => void;
  onDelete: (messageId: string) => void | Promise<void>;
};

export function TicketConversationMessages({
  messages,
  hasMoreOlder,
  loadingOlder,
  onLoadOlder,
  scrollToBottomKey,
  scrollToBottomSignal,
  onAtBottomChange,
  studentLastReadAt,
  editingMessageId,
  editText,
  onEditTextChange,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onUnsend,
  onDelete,
}: TicketConversationMessagesProps) {
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      setDeleting(true);
      await onDelete(deleteTargetId);
      setDeleteTargetId(null);
    } finally {
      setDeleting(false);
    }
  };

  const renderMessage = (m: TicketMessage) => {
    const isAdmin = m.senderType === 'admin';
    const isUnsent = Boolean(m.isUnsent && !m.isDeleted);
    const isSeen = isMessageSeenByStudent(m, studentLastReadAt);
    const timeLabel = formatTicketMessageTime(
      isUnsent && m.unsentAt ? m.unsentAt : m.createdAt
    );
    const isEditing = editingMessageId === m.messageId;

    return (
      <div className={cn('flex', isAdmin ? 'justify-end' : 'justify-start')}>
        {isEditing && isAdmin ? (
          <MessageEditCard
            editText={editText}
            onEditTextChange={onEditTextChange}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
          />
        ) : (
          <div
            className={cn(
              'relative max-w-[72%] rounded-[20px] px-4 py-3 text-sm leading-relaxed shadow-[0_8px_18px_rgba(15,23,42,0.06)]',
              isAdmin && !isUnsent && !m.isDeleted
                ? 'rounded-br-[7px] bg-gradient-to-br from-primary to-primary-to text-primary-foreground'
                : isAdmin && isUnsent
                  ? 'rounded-br-[7px] border border-dashed border-border bg-muted text-muted-foreground shadow-none'
                  : 'rounded-bl-[7px] border border-border bg-card text-foreground'
            )}
          >
            {isAdmin && !m.isDeleted && (
              <div className="absolute top-2 right-2 z-10">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'grid h-6 w-6 place-items-center rounded-full',
                        isUnsent
                          ? 'bg-muted text-muted-foreground hover:bg-muted/80'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      )}
                      aria-label="Message actions"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onStartEdit(m)}>Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onUnsend(m.messageId)}>Unsend</DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={(e) => {
                        e.preventDefault();
                        setDeleteTargetId(m.messageId);
                      }}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <span
              className={cn(
                'block pr-7',
                isUnsent && 'line-through opacity-75',
                m.isDeleted && 'italic opacity-80'
              )}
            >
              {messageDisplayText(m)}
            </span>
            {!m.isDeleted && (
              <MessageAttachments attachments={m.attachments} isAdmin={isAdmin && !isUnsent} />
            )}
            <span
              className={cn(
                'mt-1.5 block text-[11px] opacity-72',
                isAdmin && !isUnsent && !m.isDeleted ? 'text-white/80' : 'text-muted-foreground'
              )}
            >
              {isUnsent ? `Unsent ${timeLabel.toLowerCase()}` : timeLabel}
              {m.isEdited && !m.isDeleted && !isUnsent && ' · Edited'}
              {isUnsent && ' · Unsent'}
              {isAdmin && !isUnsent && !m.isDeleted && (
                <span
                  className={cn('ml-1 font-bold', isSeen ? 'text-white/90' : 'text-white/70')}
                >
                  · {isSeen ? 'Seen' : 'Not seen'}
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <VirtualTicketMessageList
        messages={messages}
        hasMoreOlder={hasMoreOlder}
        loadingOlder={loadingOlder}
        onLoadOlder={onLoadOlder}
        renderMessage={(m) => renderMessage(m)}
        scrollToBottomKey={scrollToBottomKey}
        scrollToBottomSignal={scrollToBottomSignal}
        onAtBottomChange={onAtBottomChange}
      />

      <AlertDialog
        open={Boolean(deleteTargetId)}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTargetId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              The student will see &quot;This message was deleted&quot; instead of your original text. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
